/**
 * Explicit open/closed choices, remembered per Lens. Untouched cards use
 * their result's compactness to decide whether to open. This is deliberately
 * local, the same reasoning as `lensVisits.ts`: a personal reading habit, not
 * shared board state, so it is never written to the reporting service.
 *
 * Every read and every write is guarded. Private browsing, a managed browser
 * with site data blocked, and a full quota all throw here, and none of them
 * may cost a reader the board.
 */

const LEGACY_STORAGE_KEY_PREFIX = 'askRichard:lens-rows-expanded:v1:';
const STORAGE_KEY_PREFIX = 'askRichard:lens-rows-choices:v2:';

export type RowsDrawerChoices = Readonly<Record<string, boolean>>;

function storageKey(lensId: string): string {
  return `${STORAGE_KEY_PREFIX}${lensId}`;
}

export function readRowsDrawerChoices(lensId: string): RowsDrawerChoices {
  try {
    const raw = window.localStorage.getItem(storageKey(lensId));
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return Object.fromEntries(Object.entries(parsed).filter(([, value]) => typeof value === 'boolean'));
      }
    }
    // An old expanded id is an explicit open choice. Absence from the old
    // list only meant the default, so it must not become a permanent close.
    const legacy: unknown = JSON.parse(window.localStorage.getItem(`${LEGACY_STORAGE_KEY_PREFIX}${lensId}`) ?? 'null');
    return Array.isArray(legacy)
      ? Object.fromEntries(legacy.filter((id): id is string => typeof id === 'string').map((id) => [id, true]))
      : {};
  } catch {
    return {};
  }
}

export function writeRowsDrawerChoices(lensId: string, choices: RowsDrawerChoices): void {
  try {
    window.localStorage.setItem(storageKey(lensId), JSON.stringify(choices));
  } catch {
    // Guarded: private browsing, a blocked store or a full quota must not cost a reader the board.
  }
}
